import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { supabase } from './supabaseClient';
import PublicHeader from './components/PublicHeader';

// This function should be defined globally or imported if also used by AcceptInvitationPage
// For now, duplicating it here. Consider refactoring to a shared utils/api.js file.
async function processInvitationOnBackend(token, userAccessToken) {
  console.log('[SignUp.js] Calling /process-invitation backend with token:', token);
  const response = await fetch('/functions/v1/process-invitation', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${userAccessToken}`,
      // 'apikey': 'YOUR_SUPABASE_ANON_KEY' // If needed by Nginx/Kong
    },
    body: JSON.stringify({ invitation_token: token })
  });
  const responseBody = await response.json();
  if (!response.ok) {
    console.error('[SignUp.js] Backend process-invitation error:', responseBody);
    throw new Error(responseBody.details || responseBody.error || 'Failed to process invitation after sign-up');
  }
  console.log('[SignUp.js] Backend process-invitation success after sign-up:', responseBody);
  return responseBody;
}

function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();

  // Check for plan selection from URL (from www.comturk.com/pricing)
  const selectedPlan = new URLSearchParams(window.location.search).get('plan');

  // Access location state if passed from AcceptInvitationPage
  // const location = useLocation(); 
  // const invitedEmailFromState = location.state?.invitedEmail;
  // const invitationTokenFromState = location.state?.invitationToken;
  // useEffect(() => { if (invitedEmailFromState) setEmail(invitedEmailFromState); }, [invitedEmailFromState]);

  const handleSignUp = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!email || !password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin, // Redirect to homepage after email verification
          data: { email }, // You can add more metadata here if needed
        },
      });

      if (signUpError) throw signUpError;

      // Sign-up initiated. If email confirmation is on, user verifies email.
      // If auto-confirm is on, user.id should be available in data.user.
      // We now need to get the session to have an access token.

      setSuccess('Sign-up successful! If email verification is required, please check your email. Processing invitation if present...');

      // Wait a moment for auth state to potentially update after signup
      await new Promise(resolve => setTimeout(resolve, 1000));

      const { data: { session } } = await supabase.auth.getSession();

      // const pendingToken = localStorage.getItem('pendingInvitationToken'); // No longer needed here

      // Invitation processing is now handled by AuthContext when the session becomes active.
      // SignUp.js no longer needs to call processInvitationOnBackend directly.

      if (session) {
        // If auto-confirm is ON and a session is immediately available,
        // AuthContext will handle the pending invitation token shortly after this.
        console.log('[SignUp.js] Sign-up successful, session available. AuthContext will handle invitation processing.');
        // The success message might need to be updated based on whether an invitation was pending or not,
        // but AuthContext doesn't easily communicate that back here. A general message is safer.
        setSuccess('Sign-up successful! Your account is being set up. Redirecting...');
        // If AuthContext processed an invitation, it might already want to redirect.
        // However, a general redirect to home is typical after signup.
        // App.js onboarding logic will then take over if company_id is not set.
        navigate('/');
      } else {
        // No session yet, likely email verification is pending.
        // AuthContext will handle the pending invitation token once the user verifies email and logs in (or session starts).
        console.log('[SignUp.js] Sign-up initiated. Email verification likely pending. AuthContext will handle invitation processing after verification & session start.');
        setSuccess('Sign-up initiated! Please check your email to verify your account. If you were invited, your invitation will be processed once your account is active.')
      }
      // Reset form only if everything was truly final - perhaps not here, let user see success.
      // setEmail('');
      // setPassword('');

    } catch (err) {
      console.error('Error during sign-up or post-signup processing:', err);
      setError(err.message || 'An error occurred during sign-up');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError('');
    setGoogleLoading(true);

    // Store token before OAuth redirect if it's available from URL (e.g. if user lands on /signup?invitation_token=...)
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('invitation_token');
    const planFromUrl = params.get('plan'); // Handle plan selection from www.comturk.com

    if (tokenFromUrl) {
      localStorage.setItem('pendingInvitationToken', tokenFromUrl);
      console.log('[SignUp.js] Storing invitation token from URL before Google OAuth.');
    }

    if (planFromUrl) {
      localStorage.setItem('selectedPlan', planFromUrl);
      localStorage.setItem('pendingPlanSelection', 'true');
      console.log(`[SignUp.js] Plan selected from URL: ${planFromUrl}`);
    }

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin, // OAuth will redirect back, App.js/useEffect handles session
          queryParams: {
            prompt: 'select_account' // Show account selection (industry standard)
          }
        }
      });

      if (error) throw error;
      // Redirect happens via Supabase
    } catch (err) {
      console.error('Error with Google sign-in:', err);
      setError(err.message || 'An error occurred with Google sign-in');
      setGoogleLoading(false);
    }
  };

  return (
    <Box>
      <PublicHeader />
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 3,
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            Sign Up
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {success}
            </Alert>
          )}

          <Button
            onClick={handleGoogleSignUp}
            fullWidth
            variant="outlined"
            sx={{
              mb: 3,
              py: 1,
              backgroundColor: 'white',
              color: 'text.primary',
              borderColor: 'rgba(0, 0, 0, 0.23)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderColor: 'rgba(0, 0, 0, 0.23)',
              }
            }}
            disabled={googleLoading}
            startIcon={googleLoading ? <CircularProgress size={20} /> : <GoogleIcon />}
          >
            Continue with Google
          </Button>

          <Divider sx={{ width: '100%', mb: 3 }}>
            <Typography color="text.secondary" variant="body2">
              OR
            </Typography>
          </Divider>

          <Box component="form" onSubmit={handleSignUp} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign Up with Email'}
            </Button>
          </Box>

          <Grid container justifyContent="center">
            <Grid item>
              <Link to="/login" style={{ textDecoration: 'none', color: 'primary.main' }}>
                Already have an account? Login
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

export default SignUp;
